import { useEffect, useState } from "react";
import { checkEmpty, checkEmptyObj, checkIf, checkUndefined } from "../../utils";
import moment from "moment";
import { DietaryTargetsResponse } from "../../openapi/client/model/dietary-targets-response";
import { PlanOptionSubscriptionDetailResponse } from "../../openapi/client/model/plan-option-subscription-detail-response";

export interface IValidateEntity {
  error?: boolean;
  helperText?: string;
  dirty?: boolean;
}
export interface SubscriptionValidation {
  planOption?: PlanOptionSubscriptionDetailResponse;
  startDate?: moment.Moment | string;
  assignedDays?: number;
  dietaryTargets?: DietaryTargetsResponse;
  deliveryDays?: Array<string>;
  [key: string]: any; // Allow additional properties from AdminSubscriptionDetailResponse
}

export interface IValidate {
  show?: boolean;
  error?: boolean;
  dirty?: boolean;
  message?: string;
  planOption?: IValidateEntity;
  planId?: IValidateEntity; // For form fields
  planOptionId?: IValidateEntity; // For form fields
  startDate?: IValidateEntity;
  assignedDays?: IValidateEntity;
  calories?: IValidateEntity;
  fat?: IValidateEntity;
  carb?: IValidateEntity;
  protein?: IValidateEntity;
  dietaryTargets?: {
    [field: string]: IValidateEntity;
  };
  deliveryDays?: IValidateEntity;
}

export default function useSubscriptionValidation(validationInfo: SubscriptionValidation) {
  const [validation, setValidation] = useState<IValidate>({
    show: false,
    error: false,
    dirty: false,
    message: "",
  });

  useEffect(() => {
    if (!checkIf(validationInfo) || checkEmptyObj(validationInfo)) {
      return;
    }

    setValidation((state) => {
      let newState = { ...state } as IValidate;
      if (Object.keys(validation).length === 4) {
        let fields: any = {};
        Object.keys(validationInfo).forEach((item: string) => {
          const value = validationInfo[item as keyof SubscriptionValidation];
          if (item === "dietaryTargets") {
            if (validationInfo.dietaryTargets) {
              Object.keys(validationInfo.dietaryTargets).forEach((dietaryTarget: string) => {
                fields[dietaryTarget] = { error: false, helperText: "" };
              });
            }
          } else {
            fields[item] = { error: false, helperText: "" };
          }
        });
        newState = { ...newState, ...fields };
      }

      newState.error = false;

      // Initialize planOption, planId, and planOptionId validation if they don't exist
      if (!newState.planOption) {
        newState.planOption = { error: false, helperText: "" };
      }
      if (!newState.planId) {
        newState.planId = { error: false, helperText: "" };
      }
      if (!newState.planOptionId) {
        newState.planOptionId = { error: false, helperText: "" };
      }

      // Check if planOption and its nested plan exist (for AdminSubscriptionDetailResponse)
      // OR check if planId/planOptionId exist directly (for form state)
      const hasPlanOption = !checkEmpty(validationInfo?.planOption?.plan?.id) && !checkEmpty(validationInfo?.planOption?.id);
      const planId = (validationInfo as any)?.planId;
      const planOptionId = (validationInfo as any)?.planOptionId;

      if (hasPlanOption) {
        // If using nested structure (AdminSubscriptionDetailResponse), all are valid
        newState.planOption!.error = false;
        newState.planId!.error = false;
        newState.planOptionId!.error = false;
      } else {
        // Always validate form fields if they exist
        if (checkEmpty(planId) || !checkIf(planId)) {
          newState.planId!.error = true;
          newState.planId!.helperText = "Please select a plan";
          newState.error = true;
        } else {
          newState.planId!.error = false;
        }

        if (checkEmpty(planOptionId) || !checkIf(planOptionId)) {
          newState.planOptionId!.error = true;
          newState.planOptionId!.helperText = "Please select a plan option";
          newState.error = true;
        } else {
          newState.planOptionId!.error = false;
        }

        newState.planOption!.error = false;
      }

      // Initialize startDate validation if it doesn't exist
      if (!newState.startDate) {
        newState.startDate = { error: false, helperText: "" };
      }

      if (!checkUndefined(validationInfo.startDate)) {
        if (checkEmpty(validationInfo?.startDate) || !checkIf(validationInfo?.startDate)) {
          newState.startDate!.error = true;
          newState.startDate!.helperText = "Please select the start date of the subscription";
          newState.error = true;
        } else {
          newState.startDate!.error = false;
        }
      }

      // Initialize assignedDays validation if it doesn't exist
      if (!newState.assignedDays) {
        newState.assignedDays = { error: false, helperText: "" };
      }

      if (checkEmpty(validationInfo?.assignedDays) || !checkIf(validationInfo?.assignedDays)) {
        newState.assignedDays!.error = true;
        newState.assignedDays!.helperText = "Assigned days cannot be empty";
        newState.error = true;
      } else {
        let hasError = false;
        let message = "";
        const regex = new RegExp("^[0-9]+$");
        const test = regex.test(String(validationInfo.assignedDays!));

        if (test) {
          if (validationInfo.assignedDays! < 1) {
            message = "Assigned days cannot be less than zero";
            hasError = true;
          } else {
            hasError = false;
          }
        } else {
          message = "Assigned days must include numeric characters";
          hasError = true;
        }
        newState.assignedDays!.error = hasError;
        newState.assignedDays!.helperText = message;
        if (hasError) newState.error = true;
      }

      // if (!checkUndefined(validationInfo.deliveryDays)) {
      //   if (checkEmpty(validationInfo?.deliveryDays) || !checkIf(validationInfo?.deliveryDays)) {
      //     newState.deliveryDays!.error = true;
      //     newState.deliveryDays!.helperText = "Days list cannot be empty";
      //     newState.error = true;
      //   } else {
      //     let status = !Boolean(validationInfo.deliveryDays?.length);
      //     let message = "At least one selection is required";

      //     newState.deliveryDays!.error = status;
      //     newState.deliveryDays!.helperText = message;
      //     if (status) newState.error = true;
      //   }
      // }

      // Initialize calories validation if it doesn't exist
      if (!newState.calories) {
        newState.calories = { error: false, helperText: "" };
      }

      if (!checkUndefined(validationInfo.dietaryTargets?.calories)) {
        if (
          !checkEmpty(validationInfo?.dietaryTargets?.calories) &&
          checkIf(validationInfo?.dietaryTargets?.calories)
        ) {
          let hasError = false;
          let message = "";
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          const caloriesValue = validationInfo.dietaryTargets?.calories;
          const test = caloriesValue !== undefined && Boolean(caloriesValue.toString().match(numberRegex));

          if (test) {
            if (caloriesValue! < 0) {
              message = "Target calories cannot be less than zero";
              hasError = true;
            } else if (caloriesValue! > 30000) {
              message = "Target calories should be less than 30000";
              hasError = true;
            } else {
              hasError = false;
            }
          } else {
            message = "Target calories must include numeric characters";
            hasError = true;
          }
          newState.calories!.error = hasError;
          newState.calories!.helperText = message;
          if (hasError) newState.error = true;
        } else {
          newState.calories!.error = false;
        }
      }
      // Initialize protein validation if it doesn't exist
      if (!newState.protein) {
        newState.protein = { error: false, helperText: "" };
      }

      if (!checkUndefined(validationInfo.dietaryTargets?.protein)) {
        if (!checkEmpty(validationInfo?.dietaryTargets?.protein) && checkIf(validationInfo?.dietaryTargets?.protein)) {
          let hasError = false;
          let message = "";

          const numberRegex = /^\d+(\.\d{1,3})?$/;
          const proteinValue = validationInfo.dietaryTargets?.protein;
          const test = proteinValue !== undefined && Boolean(proteinValue.toString().match(numberRegex));

          if (test) {
            if (proteinValue! < 0) {
              message = "Protein cannot be less than zero";
              hasError = true;
            } else if (proteinValue! > 1000) {
              message = "Protein should be less than 1000";
              hasError = true;
            } else {
              hasError = false;
            }
          } else {
            message = "Protein must include numeric characters";
            hasError = true;
          }
          newState.protein!.error = hasError;
          newState.protein!.helperText = message;
          if (hasError) newState.error = true;
        } else {
          newState.protein!.error = false;
        }
      }
      // Initialize carb validation if it doesn't exist
      if (!newState.carb) {
        newState.carb = { error: false, helperText: "" };
      }

      if (!checkUndefined(validationInfo.dietaryTargets?.carb)) {
        if (!checkEmpty(validationInfo?.dietaryTargets?.carb) && checkIf(validationInfo?.dietaryTargets?.carb)) {
          let hasError = false;
          let message = "";
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          const carbValue = validationInfo.dietaryTargets?.carb;
          const test = carbValue !== undefined && Boolean(carbValue.toString().match(numberRegex));
          if (test) {
            if (carbValue! < 0) {
              message = "Carb cannot be less than zero";
              hasError = true;
            } else if (carbValue! > 1000) {
              message = "Carb should be less than 1000";
              hasError = true;
            } else {
              hasError = false;
            }
          } else {
            message = "Carb must include numeric characters";
            hasError = true;
          }
          newState.carb!.error = hasError;
          newState.carb!.helperText = message;
          if (hasError) newState.error = true;
        } else {
          newState.carb!.error = false;
        }
      }
      // Initialize fat validation if it doesn't exist
      if (!newState.fat) {
        newState.fat = { error: false, helperText: "" };
      }

      if (!checkUndefined(validationInfo.dietaryTargets?.fat)) {
        if (!checkEmpty(validationInfo?.dietaryTargets?.fat) && checkIf(validationInfo?.dietaryTargets?.fat)) {
          let hasError = false;
          let message = "";
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          const fatValue = validationInfo.dietaryTargets?.fat;
          const test = fatValue !== undefined && Boolean(fatValue.toString().match(numberRegex));
          if (test) {
            if (fatValue! < 0) {
              message = "Fat cannot be less than zero";
              hasError = true;
            } else if (fatValue! > 1000) {
              message = "Fat should be less than 1000";
              hasError = true;
            } else {
              hasError = false;
            }
          } else {
            message = "Fat must include numeric characters";
            hasError = true;
          }
          newState.fat!.error = hasError;
          newState.fat!.helperText = message;
          if (hasError) newState.error = true;
        } else {
          newState.fat!.error = false;
        }
      }

      return newState;
    });
  }, [validationInfo]);
  return { validation, setValidation };
}
